import { useEffect, useId, useRef } from 'react';

// Decorative randomness only; never used for market data or prediction outcomes.
const between = (min: number, max: number) => min + Math.random() * (max - min);
function createTrajectory(entryY?: number) {
  let at = 0;
  const points = [{ x: 0, y: entryY ?? between(0.68, 0.8), at }];
  const add = (x: number, y: number, duration: number) => {
    at += duration;
    points.push({ x, y, at });
  };
  let x = between(0.065, 0.09);
  // A new pass enters at the height where the previous one left the screen.
  let y = entryY ?? between(0.12, 0.88);
  add(x, y, between(180, 420));
  let direction = Math.random() < 0.5 ? -1 : 1;
  let regimeLeft = 0;
  let burstChance = 0;
  let quietChance = 0;
  let volatility = 1;
  while (x < 1) {
    if (regimeLeft-- <= 0) {
      regimeLeft = Math.floor(between(3, 13));
      burstChance = between(0.07, 0.32);
      quietChance = between(0.08, 0.65);
      volatility = between(0.65, 1.35);
    }
    const burst = Math.random() < burstChance;
    const quiet = !burst && Math.random() < quietChance;
    if (burst) {
      // Bursts may extend a trend or reverse it, with no fixed alternation.
      direction = Math.random() < 0.5 ? -1 : 1;
    } else if (Math.random() < between(0.35, 0.75)) {
      direction *= -1;
    }
    const move = burst
      ? between(0.26, 0.52)
      : quiet
        ? between(0.005, 0.018)
        : between(0.018, 0.075) * volatility;
    y += direction * move;
    // Bounce rather than flatten at the edges of the viewport.
    if (y < 0.04) {
      y = 0.08 - y;
      direction = 1;
    } else if (y > 0.92) {
      y = 1.84 - y;
      direction = -1;
    }
    x = Math.min(
      1,
      x + (burst ? between(0.025, 0.045) : between(0.009, 0.023)),
    );
    add(
      x,
      y,
      quiet ? between(230, 380) : burst ? between(100, 180) : between(80, 170),
    );
  }
  const duration = between(9500, 11000);
  for (const point of points) point.at = (point.at / at) * duration;
  return points;
}
const FADE_MS = 450;

export function LandingChart() {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trace = useRef<SVGGElement>(null);
  const up = useRef<SVGPathElement>(null);
  const down = useRef<SVGPathElement>(null);
  const marker = useRef<SVGGElement>(null);
  const pill = useRef<SVGRectElement>(null);
  const label = useRef<SVGTextElement>(null);

  useEffect(() => {
    const node = root.current!;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let width = node.clientWidth;
    let height = node.clientHeight;
    let frame = 0;
    let elapsed = 0;
    let previous: number | undefined;
    let points = createTrajectory();
    const travelTime = () => points[points.length - 1].at;

    const draw = (time: number) => {
      const middle = height / 2;
      const left = -60;
      // Let the entire 102px marker clear both edges before wrapping.
      const span = Math.max(1, width + 120);
      let x = left;
      let y = points[0].y * height;
      let path = `M${x},${y}`;
      for (let i = 1; i < points.length; i++) {
        const start = points[i - 1];
        const end = points[i];
        const local = time - start.at;
        if (local <= 0) break;
        const progress = Math.min(1, local / (end.at - start.at));
        x = left + (start.x + (end.x - start.x) * progress) * span;
        y = (start.y + (end.y - start.y) * progress) * height;
        path += `L${x},${y}`;
        if (progress < 1) break;
      }
      const isRun = y <= middle;
      up.current!.setAttribute('d', path);
      down.current!.setAttribute('d', path);
      marker.current!.setAttribute('transform', `translate(${x},${y})`);
      pill.current!.setAttribute('fill', isRun ? '#a3e635' : '#dc2626');
      label.current!.setAttribute('fill', isRun ? '#000' : '#fff');
      label.current!.textContent = isRun ? 'Run' : 'Dead';
      node.dataset.zone = isRun ? 'run' : 'dead';
      trace.current!.style.opacity = String(
        1 - Math.min(1, Math.max(0, (time - travelTime()) / FADE_MS)),
      );
    };

    const tick = (now: number) => {
      if (previous !== undefined) elapsed += now - previous;
      if (elapsed >= travelTime() + FADE_MS) {
        const exitY = points[points.length - 1].y;
        elapsed = 0;
        points = createTrajectory(exitY);
      }
      previous = now;
      draw(elapsed);
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      previous = undefined;
      if (reducedMotion.matches) draw(elapsed || travelTime() * 0.65);
      else if (!document.hidden) frame = requestAnimationFrame(tick);
    };
    const observer = new ResizeObserver(([entry]) => {
      width = entry.contentRect.width;
      height = entry.contentRect.height;
      draw(reducedMotion.matches ? elapsed || travelTime() * 0.65 : elapsed);
    });
    observer.observe(node);
    document.addEventListener('visibilitychange', sync);
    reducedMotion.addEventListener('change', sync);
    sync();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
      reducedMotion.removeEventListener('change', sync);
    };
  }, []);

  return (
    <div className="landing-chart" ref={root} aria-hidden="true">
      <div className="landing-chart-zone is-run" />
      <div className="landing-chart-zone is-dead" />
      <div className="landing-chart-baseline" />
      <svg
        className="landing-chart-trace"
        width="100%"
        height="100%"
        focusable="false"
      >
        <defs>
          <clipPath id={`${id}-up`}>
            <rect width="100%" height="50%" />
          </clipPath>
          <clipPath id={`${id}-down`}>
            <rect y="50%" width="100%" height="50%" />
          </clipPath>
        </defs>
        <g ref={trace}>
          <path ref={up} clipPath={`url(#${id}-up)`} stroke="#a3e635" />
          <path ref={down} clipPath={`url(#${id}-down)`} stroke="#dc2626" />
          <g ref={marker}>
            <rect
              ref={pill}
              x="-51"
              y="-21"
              width="102"
              height="42"
              rx="21"
              fill="#a3e635"
            />
            <text
              ref={label}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#000"
            >
              Run
            </text>
          </g>
        </g>
      </svg>
    </div>
  );
}
